<template>
  <div class="rntrc-pix">
    <div class="rntrc-pix-qr">
      <img
        v-if="qrCodeUrl"
        :src="qrCodeUrl"
        alt="QR Code do PIX"
        class="rntrc-pix-qr-img"
      />
      <Skeleton v-else-if="codigoPix" width="220px" height="220px" />
      <div v-else class="rntrc-pix-vazio">PIX ainda não gerado</div>
    </div>
    <div class="rntrc-pix-dados">
      <div class="p-grid p-m-0">
        <div class="p-col-12 p-md-6 p-p-1">
          <small class="rntrc-rotulo">Valor do PIX (ANTT)</small>
          <div
            class="rntrc-pix-valor"
            v-text="formatMoeda(valorPix) || '---'"
          />
        </div>
        <div class="p-col-12 p-md-6 p-p-1">
          <small class="rntrc-rotulo">Vencimento</small>
          <div>
            <span v-text="formatDateTime(dataVencimentoPix) || '---'" />
            <span
              v-if="dataVencimentoPix"
              class="aet-badge p-ml-2"
              :class="vencido ? 'aet-badge--danger' : 'aet-badge--warning'"
              v-text="vencido ? 'vencido' : 'faltam ' + tempoRestante"
            />
          </div>
        </div>
      </div>
      <input-with-label
        label="PIX copia e cola"
        v-slot="l"
        :floatLabel="false"
        divClass="p-mt-2"
      >
        <div class="p-inputgroup">
          <Textarea
            :id="l.id"
            :modelValue="codigoPix ?? ''"
            :readonly="true"
            :autoResize="true"
            rows="3"
            class="rntrc-pix-codigo"
          />
          <Button
            icon="fas fa-copy"
            class="p-button-rounded p-button-text"
            v-title="'Copiar código PIX'"
            :disabled="!codigoPix"
            @click="copiarCodigo()"
          />
        </div>
      </input-with-label>
      <p class="rntrc-pix-aviso">
        O PIX é pago pelo transportador e vence em 24 h. O PIX pode levar até 1
        dia útil para virar saldo na ANTT; a verificação é automática.
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  watch,
  onMounted,
  onUnmounted,
  PropType
} from "vue";
import QRCode from "qrcode";
import { formatDateTime } from "../../modules/utils";
import { useToast } from "../../modules/message";
import {
  formatMoeda,
  formatTempoRestante
} from "../../modules/business/rntrc/rntrcTypes";

interface RntrcPixState {
  qrCodeUrl: string | null;
  agora: Date;
  interval?: number | null;
}

export default defineComponent({
  setup(props) {
    const state = reactive<RntrcPixState>({
      qrCodeUrl: null,
      agora: new Date(),
      interval: null
    });

    const gerarQrCode = () => {
      state.qrCodeUrl = null;
      if (props.codigoPix) {
        QRCode.toDataURL(props.codigoPix, {
          errorCorrectionLevel: "M",
          margin: 1,
          width: 220
        })
          .then((url: string) => {
            state.qrCodeUrl = url;
          })
          .catch(() => {
            state.qrCodeUrl = null;
          });
      }
    };

    const vencido = computed(
      () =>
        !!props.dataVencimentoPix &&
        props.dataVencimentoPix.getTime() <= state.agora.getTime()
    );

    const tempoRestante = computed(() =>
      formatTempoRestante(props.dataVencimentoPix, state.agora)
    );

    const copiarCodigo = () => {
      if (props.codigoPix) {
        window.navigator.clipboard.writeText(props.codigoPix);
        useToast().success("Código PIX copiado!");
      }
    };

    watch(
      () => props.codigoPix,
      () => gerarQrCode()
    );

    onMounted(() => {
      gerarQrCode();
      state.interval = window.setInterval(() => {
        state.agora = new Date();
      }, 30 * 1000);
    });

    onUnmounted(() => {
      if (state.interval) {
        clearInterval(state.interval);
        state.interval = null;
      }
    });

    return {
      vencido,
      tempoRestante,
      copiarCodigo,
      formatDateTime,
      formatMoeda,
      ...toRefs(state)
    };
  },
  props: {
    codigoPix: String,
    valorPix: Number,
    dataVencimentoPix: Object as PropType<Date>
  }
});
</script>

<style scoped lang="scss">
.rntrc-pix {
  display: flex;
  flex-wrap: wrap;
  gap: $space-4;
  align-items: flex-start;
}

.rntrc-pix-qr {
  flex: 0 0 220px;
  display: flex;
  justify-content: center;
}

.rntrc-pix-qr-img {
  width: 220px;
  height: 220px;
  border: 1px solid $border-color-light;
  border-radius: $radius-md;
}

.rntrc-pix-vazio {
  width: 220px;
  height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $text-muted;
  border: 1px dashed $border-color;
  border-radius: $radius-md;
}

.rntrc-pix-dados {
  flex: 1 1 280px;
  min-width: 0;
}

.rntrc-pix-valor {
  font-size: $font-size-xl;
  font-weight: $font-weight-bold;
}

.rntrc-pix-codigo {
  font-family: monospace;
  font-size: $font-size-xs;
  word-break: break-all;
}

.rntrc-rotulo {
  color: $text-muted;
}

.rntrc-pix-aviso {
  margin-top: $space-3;
  font-size: $font-size-sm;
  color: $text-secondary;
}
</style>
